import { Footer } from '@/components/footer'
import { seoInternalLinks, getSeoPage } from '@/content/seo-pages'
import { useResearchPageSeoHead } from '@/lib/research-page-seo-head'
import { currentContentLinkContext, localizeContentHref } from '@/pages/content-link-utils'

interface SeoLandingPageProps {
  slug: string
}

export function SeoLandingPage({ slug }: SeoLandingPageProps) {
  const page = getSeoPage(slug)
  useResearchPageSeoHead({
    pagePath: `/${slug}`,
    contentLanguage: 'en',
    title: page?.title ?? 'Benchmark page unavailable | Mentiss AI',
    description: page?.description ?? 'This benchmark topic is not available.',
  })
  const contentLinkContext = currentContentLinkContext()
  const localizedHref = (href: string) =>
    localizeContentHref(href, contentLinkContext) ?? href

  if (!page) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <main className="mx-auto max-w-3xl px-4 py-24" lang="en">
          <h1 className="text-3xl font-bold">SEO page not found</h1>
          <p className="mt-4 text-muted-foreground">
            This benchmark topic is not available.
          </p>
          <a className="mt-6 inline-flex text-primary underline" href={localizedHref('/')}>
            Return to Mentiss
          </a>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main lang="en">
        <section className="border-b border-border bg-slate-950 px-4 py-24 text-white">
          <div className="mx-auto max-w-5xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.22em] text-amber-300">
              {page.eyebrow}
            </p>
            <h1 className="max-w-4xl text-4xl font-bold tracking-tight md:text-5xl">
              {page.h1}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-200">
              {page.lead}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={localizedHref('https://werewolf.mentiss.ai/new')}
                className="rounded-md bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-300"
              >
                Play Werewolf with AI
              </a>
              <a
                href={localizedHref('/blogs/benchmark-report')}
                className="rounded-md border border-white/25 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Read the benchmark report
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-5xl gap-8 px-4 py-12 lg:grid-cols-[1.1fr_0.9fr]">
          <article className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <h2 className="text-2xl font-semibold">{page.answerHeading}</h2>
            <p className="mt-4 text-base leading-7 text-muted-foreground">
              {page.answer}
            </p>
          </article>

          <aside className="rounded-lg border border-border bg-muted/40 p-6">
            <h2 className="text-xl font-semibold">Related Mentiss resources</h2>
            <div className="mt-4 grid gap-3">
              {seoInternalLinks.slice(0, 5).map(link => (
                <a
                  key={link.href}
                  href={localizedHref(link.href)}
                  className="rounded-md border border-border bg-background p-3 transition hover:border-amber-400"
                >
                  <span className="block text-sm font-semibold text-foreground">
                    {link.label}
                  </span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    {link.description}
                  </span>
                </a>
              ))}
            </div>
          </aside>
        </section>

        <section className="border-y border-border bg-muted/30 px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-semibold">{page.whyHeading}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {page.why.map(item => (
                <div key={item} className="rounded-lg border border-border bg-background p-5">
                  <p className="text-base leading-7 text-muted-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-12">
          <h2 className="text-2xl font-semibold">{page.comparisonHeading}</h2>
          <div className="mt-6 overflow-hidden rounded-lg border border-border">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="p-4 font-semibold">Feature</th>
                  <th className="p-4 font-semibold">Mentiss</th>
                  <th className="p-4 font-semibold">Static benchmark</th>
                </tr>
              </thead>
              <tbody>
                {page.comparison.map(row => (
                  <tr key={row.feature} className="border-t border-border">
                    <td className="p-4 font-medium">{row.feature}</td>
                    <td className="p-4 text-muted-foreground">{row.mentiss}</td>
                    <td className="p-4 text-muted-foreground">{row.staticBenchmark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="border-y border-border bg-muted/30 px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-semibold">{page.evidenceHeading}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {page.evidence.map(item => (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-border bg-background p-5 transition hover:border-amber-400"
                >
                  <span className="block text-base font-semibold">{item.label}</span>
                  <span className="mt-3 block text-sm leading-6 text-muted-foreground">
                    {item.text}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-12">
          <h2 className="text-2xl font-semibold">Frequently asked questions</h2>
          <div className="mt-6 grid gap-6">
            {page.faqs.map(faq => (
              <article key={faq.question} className="rounded-lg border border-border p-5">
                <h3 className="text-lg font-semibold">{faq.question}</h3>
                <p className="mt-3 leading-7 text-muted-foreground">{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
